if (!customElements.get('sacred-pujas-location')) {
  customElements.define(
    'sacred-pujas-location',
    class SacredPujasLocation extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-location-trigger]');
        this.menu = this.querySelector('[data-location-menu]');
        this.value = this.querySelector('[data-location-value]');
        this.search = this.querySelector('[data-location-search]');
        this.options = Array.from(this.querySelectorAll('[data-location-option]'));
        this.noResults = this.querySelector('[data-location-no-results]');
        if (!this.trigger || !this.menu) return;

        this.handleTrigger = () => (this.isOpen ? this.close() : this.open());
        this.handleOptionClick = (event) => {
          const option = event.target.closest('[data-location-option]');
          if (option && this.contains(option)) this.select(option);
        };
        this.handleOutsideClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        this.handleSearch = () => this.filterOptions(this.search?.value || '');
        this.handleKeydown = (event) => this.onKeydown(event);

        this.trigger.addEventListener('click', this.handleTrigger);
        this.addEventListener('click', this.handleOptionClick);
        this.search?.addEventListener('input', this.handleSearch);
        this.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('click', this.handleOutsideClick);
      }

      disconnectedCallback() {
        window.clearTimeout(this.closeTimer);
        this.trigger?.removeEventListener('click', this.handleTrigger);
        this.removeEventListener('click', this.handleOptionClick);
        this.search?.removeEventListener('input', this.handleSearch);
        this.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('click', this.handleOutsideClick);
      }

      get isOpen() {
        return this.trigger?.getAttribute('aria-expanded') === 'true';
      }

      open() {
        if (!this.menu || !this.trigger || this.isOpen) return;
        window.clearTimeout(this.closeTimer);
        this.menu.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(() => this.classList.add('is-open'));
      }

      close({ restoreFocus = false, immediate = false } = {}) {
        if (!this.menu || !this.trigger) return;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        window.clearTimeout(this.closeTimer);
        const finish = () => {
          if (!this.isOpen) this.menu.hidden = true;
          if (restoreFocus) this.trigger.focus();
        };
        if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
        else this.closeTimer = window.setTimeout(finish, 180);
      }

      select(option) {
        const name = option.dataset.locationName || option.textContent.trim();
        this.options.forEach((item) => {
          const selected = item === option;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-selected', String(selected));
        });
        if (this.value) this.value.textContent = name;
        if (this.search) {
          this.search.value = '';
          this.filterOptions('');
        }
        this.dispatchEvent(new CustomEvent('location:change', { bubbles: true, detail: { name } }));
        this.close({ restoreFocus: true });
      }

      filterOptions(query) {
        const normalized = query.trim().toLocaleLowerCase();
        let visible = 0;
        this.options.forEach((option) => {
          const matches = !normalized || (option.dataset.locationName || '').toLocaleLowerCase().includes(normalized);
          option.closest('li')?.toggleAttribute('hidden', !matches);
          if (matches) visible += 1;
        });
        this.noResults?.toggleAttribute('hidden', visible !== 0);
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.isOpen) {
          event.preventDefault();
          this.close({ restoreFocus: true });
          return;
        }

        if (event.key === 'Tab' && this.isOpen) {
          this.close();
          return;
        }

        if (event.target === this.trigger && ['ArrowDown', 'Enter', ' '].includes(event.key) && !this.isOpen) {
          event.preventDefault();
          this.open();
          if (event.key === 'ArrowDown') window.requestAnimationFrame(() => (this.search || this.options[0])?.focus());
          return;
        }

        if (event.target === this.trigger && event.key === 'ArrowDown' && this.isOpen) {
          event.preventDefault();
          (this.search || this.options[0])?.focus();
          return;
        }

        if (!this.isOpen || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const visibleOptions = this.options.filter((option) => !option.closest('li')?.hidden);
        if (!visibleOptions.length) return;
        event.preventDefault();

        if (event.target === this.search) {
          visibleOptions[event.key === 'End' || event.key === 'ArrowUp' ? visibleOptions.length - 1 : 0].focus();
          return;
        }

        const currentOption = event.target.closest('[data-location-option]');
        const currentIndex = Math.max(0, visibleOptions.indexOf(currentOption));
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = visibleOptions.length - 1;
        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % visibleOptions.length;
        if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + visibleOptions.length) % visibleOptions.length;
        visibleOptions[nextIndex].focus();
      }
    }
  );
}

if (!customElements.get('sacred-pujas-browser')) {
  customElements.define(
    'sacred-pujas-browser',
    class SacredPujasBrowser extends HTMLElement {
      connectedCallback() {
        this.filters = Array.from(this.querySelectorAll('.sacred-pujas__filter'));
        this.cards = Array.from(this.querySelectorAll('.sacred-pujas__card'));
        this.status = this.querySelector('.sacred-pujas__status');
        this.empty = this.querySelector('.sacred-pujas__empty-results');
        this.results = this.querySelector('.sacred-pujas__results');
        this.handleFilterClick = this.handleFilterClick.bind(this);
        this.addEventListener('click', this.handleFilterClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleFilterClick);
      }

      handleFilterClick(event) {
        const filter = event.target.closest('.sacred-pujas__filter');
        if (!filter || !this.contains(filter)) return;
        this.setFilter(filter.dataset.filter || 'all', filter);
      }

      setFilter(selectedKey, selectedButton) {
        const key = selectedKey.trim().toLowerCase();
        let visibleCount = 0;

        this.filters.forEach((filter) => {
          const active = filter === selectedButton;
          filter.classList.toggle('is-active', active);
          filter.setAttribute('aria-pressed', String(active));
        });

        this.cards.forEach((card) => {
          const categoryKeys = (card.dataset.categories || '')
            .split(/[,|]/)
            .map((category) => category.trim().toLowerCase())
            .filter(Boolean);
          const visible = key === 'all' || categoryKeys.includes(key);
          card.hidden = !visible;
          card.setAttribute('aria-hidden', String(!visible));
          if (visible) visibleCount += 1;
        });

        this.empty?.toggleAttribute('hidden', visibleCount !== 0);
        if (this.status) {
          const collectionName = selectedButton?.lastElementChild?.textContent?.trim() || 'All';
          this.status.textContent = `${visibleCount} puja${visibleCount === 1 ? '' : 's'} shown for ${collectionName}.`;
        }

        if (window.matchMedia('(max-width: 749px)').matches) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.results?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }
      }

    }
  );
}

if (!customElements.get('sacred-pujas-trust-carousel')) {
  customElements.define(
    'sacred-pujas-trust-carousel',
    class SacredPujasTrustCarousel extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.sacred-pujas__trust-item'));
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.autoplay = this.dataset.autoplay === 'true';
        this.delay = Math.max(2000, Number(this.dataset.delay) || 3000);
        this.timer = null;
        this.visible = true;

        this.handleModeChange = () => this.updateAutoplay();
        this.handlePointerDown = () => this.stopAutoplay();
        this.handlePointerUp = () => this.startAutoplay();
        this.handleFocusIn = () => this.stopAutoplay();
        this.handleFocusOut = () => this.startAutoplay();
        this.handleVisibility = () => this.updateAutoplay();

        this.mobileQuery.addEventListener('change', this.handleModeChange);
        this.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        this.addEventListener('pointerup', this.handlePointerUp, { passive: true });
        this.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
        this.addEventListener('mouseenter', this.handlePointerDown);
        this.addEventListener('mouseleave', this.handlePointerUp);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('visibilitychange', this.handleVisibility);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              this.visible = entry.isIntersecting;
              this.updateAutoplay();
            },
            { threshold: 0.15 }
          );
          this.observer.observe(this);
        }

        this.updateAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.mobileQuery?.removeEventListener('change', this.handleModeChange);
        this.removeEventListener('pointerdown', this.handlePointerDown);
        this.removeEventListener('pointerup', this.handlePointerUp);
        this.removeEventListener('pointercancel', this.handlePointerUp);
        this.removeEventListener('mouseenter', this.handlePointerDown);
        this.removeEventListener('mouseleave', this.handlePointerUp);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        document.removeEventListener('visibilitychange', this.handleVisibility);
        this.observer?.disconnect();
      }

      updateAutoplay() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canPlay =
          this.autoplay &&
          this.mobileQuery.matches &&
          this.items.length > 1 &&
          this.visible &&
          !document.hidden &&
          !reduceMotion;

        if (canPlay) this.startAutoplay();
        else this.stopAutoplay();
      }

      startAutoplay() {
        this.stopAutoplay();
        if (
          !this.autoplay ||
          !this.mobileQuery.matches ||
          this.items.length < 2 ||
          !this.visible ||
          document.hidden ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) return;

        this.timer = window.setInterval(() => this.showNext(), this.delay);
      }

      stopAutoplay() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
      }

      showNext() {
        const firstOffset = this.items[0].offsetLeft;
        const current = this.items.reduce(
          (closest, item, index) => {
            const distance = Math.abs(item.offsetLeft - firstOffset - this.scrollLeft);
            return distance < closest.distance ? { index, distance } : closest;
          },
          { index: 0, distance: Infinity }
        ).index;
        const next = (current + 1) % this.items.length;

        this.scrollTo({ left: this.items[next].offsetLeft - firstOffset, behavior: 'smooth' });
      }
    }
  );
}
